import { cx } from '@emotion/css';
import { Theme, Typography, TypographyProps } from '@mui/material';
import { CSSProperties } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  TypographyClamped,
  TypographyClampedProps,
} from '@/components/typography/TypographyClamped';
import { GraphVizTileEmphasis } from '@/modules/graphViz/graphVizNodeConfig';
import { FONT_SIZING } from '@/styles/tokens/fonts';

import { getTextColorForVariant } from './Tile.utils';
import { TileFontSizeVariant, TileVariant } from './types';

const FONT_SIZE_ADJUSTMENTS: Record<TileFontSizeVariant, number> = {
  SM: 0,
  MD: 0.5,
  LG: 1,
  XL: 1.5,
  XXL: 2,
} as const;

/**
 * Returns the ratio between the adjusted font size for a tile font size
 * variant and the base font size of a typography variant. Use it to scale
 * non-text elements (like icons) in step with the tile text.
 */
export function getTileFontScale(
  theme: Theme,
  variant: keyof Theme['typography'],
  fontSizeVariant: TileFontSizeVariant
): number {
  const typographyStyles = theme.typography[variant] as CSSProperties;
  const baseFontSizeRem = parseRemValue(
    typographyStyles?.fontSize ?? FONT_SIZING.md.fontSize
  );
  return (
    (baseFontSizeRem + FONT_SIZE_ADJUSTMENTS[fontSizeVariant]) / baseFontSizeRem
  );
}

const parseRemValue = (value: string | number): number => {
  if (typeof value === 'number') return value;

  const remValue = value.match(/^([\d.]+)rem$/)?.[1];
  if (!remValue) {
    throw new Error(`Invalid rem value: ${value}. Expected format: "1.5rem"`);
  }

  const parsed = parseFloat(remValue);
  if (isNaN(parsed)) {
    throw new Error(`Failed to parse rem value: ${value}`);
  }

  return parsed;
};

export interface TileThemedTypographyProps extends TypographyProps {
  tileVariant: TileVariant;
  fontSizeVariant: TileFontSizeVariant;
  tileEmphasis?: GraphVizTileEmphasis;
  /** A hex color that replaces the palette of `tileVariant`. */
  tileCustomColor?: string;
}

const useStyles = makeStyles<{
  tileVariant: TileVariant;
  fontSizeVariant: TileFontSizeVariant;
  tileEmphasis?: GraphVizTileEmphasis;
  tileCustomColor?: string;
  variant?: keyof Theme['typography'];
}>()((
  theme,
  { tileVariant, fontSizeVariant, tileEmphasis, tileCustomColor, variant }
) => {
  const typographyStyles = variant
    ? (theme.typography[variant] as CSSProperties)
    : null;

  const baseFontSize = typographyStyles?.fontSize ?? FONT_SIZING.md.fontSize;
  const baseLineHeight =
    typographyStyles?.lineHeight ?? FONT_SIZING.md.lineHeight;

  const baseFontSizeRem = parseRemValue(baseFontSize);
  const baseLineHeightRem = parseRemValue(baseLineHeight);

  const adjustment = FONT_SIZE_ADJUSTMENTS[fontSizeVariant];
  const fontSize = baseFontSizeRem + adjustment;
  const lineHeight = baseLineHeightRem + adjustment;

  const color = getTextColorForVariant(
    theme,
    tileVariant,
    tileEmphasis,
    tileCustomColor
  );

  return {
    root: {
      color,
      fontSize: `${fontSize}rem`,
      lineHeight: `${lineHeight}rem`,
      // Style overrides for text editor content
      '& p, & li, & ul, & ol': {
        color,
        fontSize: `${fontSize}rem`,
        lineHeight: `${lineHeight}rem`,
      },
    },
  };
});

export const TileThemedTypography = ({
  tileVariant,
  fontSizeVariant,
  tileEmphasis,
  tileCustomColor,
  variant,
  className,
  ...otherProps
}: TileThemedTypographyProps) => {
  const { classes } = useStyles({
    tileVariant,
    fontSizeVariant,
    tileEmphasis,
    tileCustomColor,
    variant: variant as keyof Theme['typography'],
  });
  return (
    <Typography
      {...otherProps}
      variant={variant}
      className={cx(classes.root, className)}
    />
  );
};

export interface TileThemedTypographyClampedProps
  extends TypographyClampedProps {
  tileVariant: TileVariant;
  fontSizeVariant: TileFontSizeVariant;
  tileEmphasis?: GraphVizTileEmphasis;
  /** A hex color that replaces the palette of `tileVariant`. */
  tileCustomColor?: string;
}

export const TileThemedTypographyClamped = ({
  tileVariant,
  fontSizeVariant,
  tileEmphasis,
  tileCustomColor,
  className,
  variant,
  ...otherProps
}: TileThemedTypographyClampedProps) => {
  const { classes } = useStyles({
    tileVariant,
    fontSizeVariant,
    tileEmphasis,
    tileCustomColor,
    variant: variant as keyof Theme['typography'],
  });

  return (
    <TypographyClamped
      {...otherProps}
      variant={variant}
      className={cx(classes.root, className)}
    />
  );
};
